import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';

interface ProfileContextType {
  profile: any;
  setProfile: (profile: any) => void;
  clearProfile: () => void;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

interface ProfileProviderProps {
  children: ReactNode;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  // Load initial profile from localStorage if available
  const [profile, setProfileState] = useState<any>(() => {
    const storedProfile = localStorage.getItem('profile');
    return storedProfile ? JSON.parse(storedProfile) : null;
  });

  // Memoized to prevent infinite re-renders in useEffect dependencies
  const setProfile = useCallback((profileData: any) => {
    setProfileState(profileData);
    if (profileData) {
      localStorage.setItem('profile', JSON.stringify(profileData));
    } else {
      localStorage.removeItem('profile');
    }
  }, []);

  // Memoized to prevent infinite re-renders in useEffect dependencies
  const clearProfile = useCallback(() => {
    setProfileState(null);
    localStorage.removeItem('profile');
  }, []);

  // Optional: keep localStorage in sync if profile changes elsewhere
  useEffect(() => {
    if (profile) {
      localStorage.setItem('profile', JSON.stringify(profile));
    } else {
      localStorage.removeItem('profile');
    }
  }, [profile]);

  return (
    <ProfileContext.Provider value={{ profile, setProfile, clearProfile }}>
      {children}
    </ProfileContext.Provider>
  );
}; 